import { serviceRequest } from './service-request';
import { StripeBusinessType } from '@cognitoforms/types/server-types/core/data-transfer/payment/cognito-pay/stripe-business-type';
import { StripeCompanyStructure } from '@cognitoforms/types/server-types/core/data-transfer/payment/cognito-pay/stripe-company-structure';
import type { PaymentMethodsResponse } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/payment-methods-response';
import type { UpdatePaymentMethodsRequest } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/update-payment-methods-request';
import type { SmartDisputesSettingsResponse } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/smart-disputes-settings-response';
import type { UpdateSmartDisputesSettingsRequest } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/update-smart-disputes-settings-request';
import type { PaymentMethodDomainsResponse } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/payment-method-domains-response';
import type { LinkableCognitoPayAccount } from '@cognitoforms/types/server-types/core/data-transfer/payment/cognito-pay/linkable-cognito-pay-account';
import type { CognitoPayOnboardingContextResponse } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/cognito-pay-onboarding-context-response';
import type { CognitoPayOnboardingStatusResponse } from '@cognitoforms/types/server-types/services/controllers/svc-controllers/payment/cognito-pay-onboarding-status-response';
import type { CognitoPayPurgeTarget } from '@cognitoforms/types/server-types/core/services/payment/cognito-pay-purge-target';
import type { CognitoPaySuggestedIndustryResponse } from '@cognitoforms/types/server-types/core/data-transfer/payment/cognito-pay/cognito-pay-suggested-industry-response';

export { StripeBusinessType, StripeCompanyStructure };
export type { PaymentMethodsResponse, UpdatePaymentMethodsRequest, SmartDisputesSettingsResponse, UpdateSmartDisputesSettingsRequest, PaymentMethodDomainsResponse, CognitoPayOnboardingContextResponse, CognitoPayOnboardingStatusResponse };

export interface CognitoPayAvailability {
	Available: boolean;
	FeatureFlagEnabled: boolean;
	IsCountrySupported: boolean;
	CountryCode: string | null;
	CanCreateOAuthStripeAccount: boolean;
	PublishableKey: string | null;
}

export interface CreateCognitoPayAccountRequest {
	Country?: string;
	BusinessType: StripeBusinessType;
	CompanyStructure?: StripeCompanyStructure;
	SuggestedMcc?: string | null;
}

export interface CreateCognitoPayAccountResponse {
	PaymentAccountId: string;
	StripeConnectedAccountId: string;
	ChargesEnabled: boolean;
	PayoutsEnabled: boolean;
	DetailsSubmitted: boolean;
}

export interface AccountSessionResponse {
	ClientSecret: string;
}

export interface CognitoPayAccountSummary {
	Id: string;
	Name: string;
	ChargesEnabled: boolean;
	ClosedUtc: string | null;
	IsClosed: boolean;
	HasRecentPaymentActivity: boolean;
	IsRemoved: boolean;
	IsRemovable: boolean;
	IsConnectedToMultipleOrgs: boolean;
}

export interface GetCognitoPayAccountsResponse {
	Accounts: CognitoPayAccountSummary[];
	PublishableKey: string | null;
}

export interface CognitoPayAccountDetails {
	BusinessName: string | null;
	BusinessType: string | null;
	Website: string | null;
	ProductDescription: string | null;
	Country: string | null;
	StatementDescriptor: string | null;
	RepresentativeName: string | null;
	RepresentativeEmail: string | null;
}

export function checkCognitoPayAvailability(): Promise<CognitoPayAvailability> {
	return serviceRequest('svc/cognito-pay/availability', null, { method: 'GET' });
}

export function createCognitoPayAccount(
	request: CreateCognitoPayAccountRequest
): Promise<CreateCognitoPayAccountResponse> {
	return serviceRequest('svc/cognito-pay/accounts', request, { method: 'POST' });
}

export function getCognitoPaySuggestedIndustry(): Promise<CognitoPaySuggestedIndustryResponse | null> {
	return serviceRequest('svc/cognito-pay/suggested-industry', null, { method: 'GET' });
}

export function getCognitoPayAccounts(logAccess = false): Promise<GetCognitoPayAccountsResponse> {
	return serviceRequest('svc/cognito-pay/accounts', null, { method: 'GET', query: logAccess ? { logAccess: 'true' } : undefined });
}

export function getLinkableCognitoPayAccounts(): Promise<LinkableCognitoPayAccount[]> {
	return serviceRequest('svc/cognito-pay/accounts/linkable', null, { method: 'GET' });
}

export function linkCognitoPayAccount(externalSourceId: string): Promise<CreateCognitoPayAccountResponse> {
	return serviceRequest('svc/cognito-pay/accounts/link', { ExternalSourceId: externalSourceId }, { method: 'POST' });
}

export function removeCognitoPayAccount(paymentAccountId: string): Promise<void> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}`, null, { method: 'DELETE' });
}

export function closeCognitoPayAccount(paymentAccountId: string): Promise<void> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/close`, null, { method: 'POST' });
}

export function reactivateCognitoPayAccount(paymentAccountId: string): Promise<void> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/reactivate`, null, { method: 'POST' });
}

export function createAccountSession(
	paymentAccountId: string
): Promise<AccountSessionResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/session`, null, { method: 'POST' });
}

export function getCognitoPayOnboardingContext(
	paymentAccountId: string
): Promise<CognitoPayOnboardingContextResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/onboarding`, null, { method: 'GET' });
}

export function refreshCognitoPayOnboardingStatus(
	paymentAccountId: string
): Promise<CognitoPayOnboardingStatusResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/onboarding/status`, null, { method: 'POST' });
}

export function getCognitoPayAccountDetails(
	paymentAccountId: string
): Promise<CognitoPayAccountDetails> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/details`, null, { method: 'GET' });
}

export function getPaymentMethods(paymentAccountId: string): Promise<PaymentMethodsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/payment-methods`, null, { method: 'GET' });
}

export function updatePaymentMethods(paymentAccountId: string, request: UpdatePaymentMethodsRequest): Promise<PaymentMethodsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/payment-methods`, request, { method: 'PUT' });
}

export function getSmartDisputesSettings(paymentAccountId: string): Promise<SmartDisputesSettingsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/smart-disputes`, null, { method: 'GET' });
}

export function updateSmartDisputesSettings(paymentAccountId: string, request: UpdateSmartDisputesSettingsRequest): Promise<SmartDisputesSettingsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/smart-disputes`, request, { method: 'PUT' });
}

export function getPaymentMethodDomains(paymentAccountId: string): Promise<PaymentMethodDomainsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/payment-method-domains`, null, { method: 'GET' });
}

export function addPaymentMethodDomains(paymentAccountId: string, domains: string[]): Promise<PaymentMethodDomainsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/payment-method-domains`, { Domains: domains }, { method: 'POST' });
}

export function disablePaymentMethodDomains(paymentAccountId: string, domains: string[]): Promise<PaymentMethodDomainsResponse> {
	return serviceRequest(`svc/cognito-pay/accounts/${paymentAccountId}/payment-method-domains`, { Domains: domains }, { method: 'DELETE' });
}

// 💀 DO NOT USE THESE UNLESS TESTING DELETING COGNITO PAY COSMOS DATA 💀

export function queueCognitoPayPartitionPurge(target: CognitoPayPurgeTarget, paymentAccountId?: string): Promise<string> {
	return serviceRequest(`svc/cognito-pay/queue-purge/${target}`, null, { method: 'POST', query: paymentAccountId ? { paymentAccountId } : undefined });
}

export function testCognitoPayPartitionPurge(target: CognitoPayPurgeTarget, paymentAccountId?: string): Promise<string> {
	return serviceRequest(`svc/cognito-pay/test-purge/${target}`, null, { method: 'POST', query: paymentAccountId ? { paymentAccountId } : undefined });
}
